import type { ReportEvent } from '@/types/report';
import type { ReportGroupByMode } from '../constants';

export const REPORT_EVENT_GROUP_EMPTY_KEY = '__empty__';

export interface ReportEventGroup<T extends ReportEvent = ReportEvent> {
    key: string;
    events: T[];
    sectionLabel?: string;
}

function bucketKey(
    event: ReportEvent,
    mode: Exclude<ReportGroupByMode, 'none'>,
): string {
    if (mode === 'prompt') {
        const trimmed = event.promptText.trim();
        return trimmed.length === 0
            ? REPORT_EVENT_GROUP_EMPTY_KEY
            : event.promptId;
    }
    const trimmed = event.sourceId.trim();
    return trimmed.length === 0 ? REPORT_EVENT_GROUP_EMPTY_KEY : trimmed;
}

export function groupReportEvents<T extends ReportEvent = ReportEvent>(
    events: T[],
    mode: Exclude<ReportGroupByMode, 'none'>,
): ReportEventGroup<T>[] {
    const buckets = new Map<string, T[]>();
    for (const event of events) {
        const key = bucketKey(event, mode);
        const existing = buckets.get(key);
        if (existing) {
            existing.push(event);
        } else {
            buckets.set(key, [event]);
        }
    }
    const groups: ReportEventGroup<T>[] = Array.from(buckets.entries()).map(
        ([key, groupedEvents]) => {
            if (key === REPORT_EVENT_GROUP_EMPTY_KEY) {
                return { key, events: groupedEvents };
            }
            if (mode === 'prompt') {
                return {
                    key,
                    events: groupedEvents,
                    sectionLabel: groupedEvents[0]!.promptText.trim(),
                };
            }
            const firstEvent = groupedEvents[0]!;
            const name = firstEvent.sourceName.trim();
            return {
                key,
                events: groupedEvents,
                sectionLabel: name.length > 0 ? name : firstEvent.sourceId,
            };
        },
    );
    groups.sort((first, second) => {
        if (first.key === REPORT_EVENT_GROUP_EMPTY_KEY) {
            return second.key === REPORT_EVENT_GROUP_EMPTY_KEY ? 0 : 1;
        }
        if (second.key === REPORT_EVENT_GROUP_EMPTY_KEY) {
            return -1;
        }
        if (mode === 'prompt') {
            return (
                first.events[0]!.promptPosition -
                second.events[0]!.promptPosition
            );
        }
        const firstSort = first.sectionLabel ?? first.key;
        const secondSort = second.sectionLabel ?? second.key;
        return firstSort.localeCompare(secondSort, undefined, {
            sensitivity: 'base',
        });
    });
    return groups;
}
